import axios from 'axios';
import history from '../history';
import { getOpenCartItemsThunk } from './cartItems';
import { savePendingAddressIfAny } from '../utils/pendingAddress';

/**
 * ACTION TYPES
 */
const GET_USER = 'GET_USER';
const REMOVE_USER = 'REMOVE_USER';

/**
 * INITIAL STATE
 */
const defaultUser = {};

/** Ignore stale auth responses when the user submits more than once. */
let authRequestId = 0;
/** Invalidate in-flight /auth/me so it can't overwrite a login/signup result. */
let sessionHydrationId = 0;

/**
 * ACTION CREATORS
 */
const getUser = (user) => ({ type: GET_USER, user });
const removeUser = () => ({ type: REMOVE_USER });

/**
 * THUNK CREATORS
 */
export const me = () => async (dispatch, getState) => {
  const hydrationId = sessionHydrationId;
  try {
    const res = await axios.get('/auth/me');
    // A login/signup happened while this request was in flight — ignore.
    if (hydrationId !== sessionHydrationId) return;
    // Keep signup/login form errors visible; don't restore a session over them.
    if (getState().user && getState().user.error) return;
    dispatch(getUser(res.data || defaultUser));
  } catch (err) {
    console.error(err);
  }
};

export const auth = (email, password, method, firstName, lastName) => async (
  dispatch
) => {
  const requestId = ++authRequestId;
  // Any /auth/me started before this must not clobber the outcome.
  sessionHydrationId += 1;

  let res;
  try {
    res = await axios.post(`/auth/${method}`, {
      email,
      password,
      firstName,
      lastName,
    });
  } catch (authError) {
    if (requestId !== authRequestId) return;
    // Show the error only — never treat this as a logged-in session.
    return dispatch(getUser({ error: authError }));
  }

  // A newer login/signup attempt superseded this one.
  if (requestId !== authRequestId) return;

  try {
    dispatch(getUser(res.data));
    // Guest cart was merged server-side — refresh Redux cart.
    await dispatch(getOpenCartItemsThunk());
    if (method === 'signup') {
      await savePendingAddressIfAny(axios);
    }
    if (requestId !== authRequestId) return;
    history.push(method === 'signup' ? '/user' : '/');
  } catch (dispatchOrHistoryErr) {
    console.error(dispatchOrHistoryErr);
  }
};

export const logout = () => async (dispatch) => {
  try {
    sessionHydrationId += 1;
    await axios.delete('/auth/logout');
    dispatch(removeUser());
    history.push('/login');
  } catch (err) {
    console.error(err);
  }
};

export const forgot = (email) => async (dispatch) => {
  try {
    const { data } = await axios.post('/auth/forgot-password', { email });
    dispatch(getUser({ error: null, alert: data }));
  } catch (wrongEmailError) {
    return dispatch(getUser({ error: wrongEmailError }));
  }
};

export const reset = (password, token) => async (dispatch) => {
  try {
    const { data } = await axios.post('/auth/reset', { password, token });
    dispatch(getUser({ error: null, alert: data }));
  } catch (invalidTokenError) {
    return dispatch(getUser({ error: invalidTokenError }));
  }
};

/**
 * REDUCER
 */
export default function (state = defaultUser, action) {
  switch (action.type) {
    case GET_USER:
      return action.user;
    case REMOVE_USER:
      return defaultUser;
    default:
      return state;
  }
}
